import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadSpoofCandidates } from 'actions/admin';
import ReturnToMe from 'components/returnToMe';
import './spoof.css';
import SimpleSpoof, { DefaultOptions } from 'components/simpleSpoof';
import { Help, SystemText, DebugInfo } from '@salesperformanceint/e4shared';

export const SpoofUsers = props => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        // This page is only for PRISM partner/spoofing only
        if (!user.companySettings?.prism_UI || (!user?.partner_Id > 0 && !user?.spoofing)) {
            props.history.replace('/');
        }
    }, [user]);

    const text = {
        table: {
            options: {
                user: {
                    label: SystemText('Spoof/User/Table/Options/User/Label'),
                },
                spoof: {
                    label: SystemText('Spoof/User/Table/Options/Spoof/Label'),
                },
            },
            columns: {
                user: SystemText('Spoof/User/Table/Columns/User'),
                email: SystemText('Spoof/User/Table/Columns/Email'),
                administrator: SystemText('Spoof/User/Table/Columns/Administrator'),
                levels: {
                    admin: SystemText('Spoof/User/Table/Columns/Permission/Admin'),
                    config: SystemText('Spoof/User/Table/Columns/Permission/Config'),
                },
            },
        },
        filter: {
            help: SystemText('Spoof/User/Filter/Help'),
        },
        current: SystemText('Spoof/User/Table/Current'),
    };

    const allProfiles = useSelector(state => state.admin.spoofCandidates) || [];

    // While spoofing, the spoofed user's row leads the list and carries Return to me.
    const data = allProfiles?.map(p => ({
        ...p,
        zero: p.licence === 'ZERO',
        current: !!user.spoofing && p.id === user.id,
    }));

    // The list leaves out support users, who can still be spoofed; without their row, Return to me
    // goes in the intro instead.
    const returnInIntro = !!user.spoofing && !data.some(d => d.current);

    React.useEffect(() => {
        api.load();
    }, []);

    React.useEffect(() => {
        DebugInfo('component', 'SpoofUsers');
    }, []);

    const api = {
        load: () => {
            dispatch(loadSpoofCandidates());
        },
    };

    const spoofUser = userToSpoof => {
        props.history.push?.(`/spoofing?email=${encodeURIComponent(userToSpoof.email)}&spoofing=${user.spoofing}`);
    };

    const options = {
        ...DefaultOptions(),
        sortBy: 'list_Name',
        showActive: false, // must be false to remove activate/deactivate button
        label: text.table.options.user.label,
        mainColumnIndex: 1,
        showBulk: true,
        trClass: d => (d.current ? 'spoof-current' : ''),
        extraOptions: [
            {
                btnClass: 'btn-primary',
                label: text.table.options.spoof.label, // "Spoof" to add to name to get e.g. "Spoof Sara Brown" on hover
                icon: 'fa-external-link',
                disabled: d => d.id === user.id || d.zero,
                onClick: d => spoofUser(d),
                renderInstead: d => d.current && <ReturnToMe className="btn btn-sm btn-primary" />,
            },
        ],
    };

    let tableColumns = [
        {
            label: text.table.columns.user,
            dataProperty: 'list_Name',
            searchProperty: 'full_Name',
            sortByFunction: (data, asc) => {
                const regPrefix = asc ? '1' : '0';
                const dir = asc ? 1 : -1;

                return data
                    .map(d => {
                        const sortPrefix = regPrefix;

                        return { ...d, sortProperty: `${sortPrefix}-${d.list_Name}` };
                    })
                    .sort((a, b) => (a.sortProperty > b.sortProperty ? 1 * dir : -1 * dir));
            },
            format: (v, d) =>
                d.current ? (
                    <>
                        {v}
                        <span className="spoof-current__badge">{text.current}</span>
                    </>
                ) : (
                    v
                ),
            className: 'no-wrap',
        },
        { label: text.table.columns.email, dataProperty: 'email', searchProperty: 'email' },
        {
            label: text.table.columns.administrator,
            dataProperty: 'administrator',
            format: (v, d) => {
                const hover =
                    d.licence === 'ZERO'
                        ? ''
                        : d.administrator
                          ? text.table.columns.levels.admin
                          : d.sys_Config
                            ? text.table.columns.levels.config
                            : '';

                return hover ? (
                    <Help id={`help-admin-${d.id}`} target={<i className="fa fa-user-shield" />}>
                        {hover}
                    </Help>
                ) : (
                    <></>
                );
            },
        },
    ];

    return (
        <div>
            <SimpleSpoof
                {...props}
                data={data}
                options={options}
                columns={tableColumns}
                filterHelp={text.filter.help}
                filterColumn="active"
                pinned={d => d.current}
                intro={
                    user.spoofing && (
                        <>
                            {user.spoofedByName && (
                                <p className="spoof-signed-in-as">{SystemText('Spoof/Spoofing/SignedInAs', user.spoofedByName)}</p>
                            )}
                            {returnInIntro && <ReturnToMe className="btn btn-sm btn-primary" />}
                        </>
                    )
                }
            />
        </div>
    );
};

export default SpoofUsers;
